import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import MainCard from 'components/MainCard';
import { clientPortalInvoiceService } from 'services/api';
import { format } from 'date-fns';
import { useSnackbar } from 'hooks/useSnackbar';

const statusColors = {
  sent: 'warning',
  paid: 'success'
};

const BillingInvoiceDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showError } = useSnackbar();

  useEffect(() => {
    fetchInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const data = await clientPortalInvoiceService.getById(id);
      setInvoice(data);
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      showError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/client/billing-invoices');
  };

  const handleDownloadPdf = async () => {
    try {
      const blob = await clientPortalInvoiceService.exportPdf(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${invoice.custom_invoice_number || invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      showError('Failed to download PDF');
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'sent':
        return 'Pending Payment';
      case 'paid':
        return 'Remittance Advice';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <MainCard>
        <Typography>Loading...</Typography>
      </MainCard>
    );
  }

  if (!invoice) {
    return (
      <MainCard>
        <Typography>Invoice not found</Typography>
      </MainCard>
    );
  }

  return (
    <MainCard>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">Invoice Details</Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<ArrowLeftOutlined />} onClick={handleBack}>
              Back to Invoices
            </Button>
            <Button variant="contained" color="primary" startIcon={<DownloadOutlined />} onClick={handleDownloadPdf}>
              Download PDF
            </Button>
          </Stack>
        </Box>

        {/* Invoice Information */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Invoice Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <strong>Invoice Number</strong>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1 }}>{invoice.custom_invoice_number || invoice.invoice_number}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <Chip label={getStatusLabel(invoice.status)} size="small" color={statusColors[invoice.status]} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <strong>Invoice Date</strong>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        {invoice.invoice_date ? format(new Date(invoice.invoice_date), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <strong>Due Date</strong>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        {invoice.due_date ? format(new Date(invoice.due_date), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                    </TableRow>
                    {invoice.paid_at && (
                      <TableRow>
                        <TableCell sx={{ border: 'none', py: 1 }}>
                          <strong>Paid On</strong>
                        </TableCell>
                        <TableCell sx={{ border: 'none', py: 1 }}>{format(new Date(invoice.paid_at), 'MMM dd, yyyy')}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <strong>Project</strong>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1 }}>{invoice.project?.name || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <strong>Currency</strong>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1 }}>{invoice.currency || '-'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <strong>Total Amount</strong>
                      </TableCell>
                      <TableCell sx={{ border: 'none', py: 1 }}>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Period (if applicable) */}
          {(invoice.billing_period_start || invoice.billing_period_end) && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Billing Period
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1">
                    {invoice.billing_period_start && format(new Date(invoice.billing_period_start), 'MMM dd, yyyy')}
                    {' - '}
                    {invoice.billing_period_end && format(new Date(invoice.billing_period_end), 'MMM dd, yyyy')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Description */}
          {invoice.description && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Description
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {invoice.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Line Items */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Invoice Items
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Description</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Quantity</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Unit</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Rate</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Amount</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.line_items && invoice.line_items.length > 0 ? (
                      <>
                        {invoice.line_items.map((lineItem, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2">{lineItem.description}</Typography>
                              {lineItem.billing_period_start && lineItem.billing_period_end && (
                                <Typography variant="caption" color="textSecondary">
                                  Period: {format(new Date(lineItem.billing_period_start), 'MMM dd')} -{' '}
                                  {format(new Date(lineItem.billing_period_end), 'MMM dd, yyyy')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">{lineItem.billing_units}</TableCell>
                            <TableCell align="center">{lineItem.billing_unit_type}</TableCell>
                            <TableCell align="right">{formatCurrency(lineItem.billing_rate, invoice.currency)}</TableCell>
                            <TableCell align="right">
                              <strong>{formatCurrency(lineItem.amount, invoice.currency)}</strong>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={4} align="right">
                            <Typography variant="h6">Total:</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="primary">
                              {formatCurrency(invoice.amount, invoice.currency)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="textSecondary">No line items</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Terms */}
          {invoice.payment_terms_template && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment Terms
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {invoice.payment_terms_template.content}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Company Details */}
          {invoice.company_details_template && (
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Company Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {invoice.company_details_template.content}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Stack>
    </MainCard>
  );
};

export default BillingInvoiceDetails;
